import React, { useEffect, useState, useMemo } from 'react';
import { cleanDisplayValue } from '../../utils/displayUtils';
import {
  Box,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Paper,
  Button,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  TablePagination,
  Stack,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  Business as BusinessIcon,
  CheckCircle as CompletedIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as PendingIcon,
  PlayArrow as InProgressIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { clientAPI } from '../../utils/api';
import MetaTags from '../../components/MetaTags';
import MarqueeChip from '../../components/MarqueeChip';
import {
  CONTACT_STATUS_OPTIONS,
  CALL_OUTCOME_OPTIONS,
  LEAD_QUALIFICATION_OPTIONS,
  getOptionLabel
} from '../../constants/fieldOptions';
import CustomSelect from '../../components/CustomSelect';
import './client-css/DashboardEditor-light.css';
import './client-css/DashboardEditor-dark.css';
import './client-css/client-dashboard-light.css';
import './client-css/client-dashboard-dark.css';

interface Assignment {
  _id: string;
  assignmentId: string;
  companyId: {
    _id: string;
    companyName: string;
    companyIndustry: string;
  };
  companyName: string;
  leadCompanyName?: string; // The specific lead company assigned (e.g., "AW Rostamani")
  title: string;
  description?: string;
  status: 'active' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  totalLeads: number;
  remainingLeads: number;
  isReassignment: boolean;
  createdAt: string;
  dueDate?: string;
}

interface Lead {
  leadId: string;
  originalData: { [key: string]: any };
  // Previous values (for reassignments)
  prevContactStatus: string;
  prevCallOutcome: string;
  prevLeadQualification: string;
  prevCompletedBy?: string;
  prevCompletedAt?: string;
  prevIsCompleted?: boolean;
  // New values (for current assignment)
  newContactStatus: string;
  newCallOutcome: string;
  newLeadQualification: string;
  // Legacy fields for backward compatibility
  contactStatus?: string;
  callOutcome?: string;
  leadQualification?: string;
  _completedBy?: string;
  _isCompleted?: boolean;
  _completedAt?: string;
}

interface LeadEdit {
  newContactStatus: string;
  newCallOutcome: string;
  newLeadQualification: string;
  editorNotes?: string;
}

// Helper function to get merged name from first and last name
const getMergedName = (lead: any): string => {
  const firstName = lead['First Name'];
  const lastName = lead['Last Name'];
  const name = lead['Name'];
  
  // If Name column has a value, use it
  if (name && name !== 'N/A' && name !== '') {
    return name;
  }
  
  // Otherwise, merge First Name and Last Name
  const hasFirstName = firstName && firstName !== 'N/A' && firstName !== '';
  const hasLastName = lastName && lastName !== 'N/A' && lastName !== '';
  
  if (hasFirstName && hasLastName) {
    return `${firstName} ${lastName}`;
  } else if (hasFirstName) {
    return firstName;
  } else if (hasLastName) {
    return lastName;
  }
  
  return 'N/A';
};

// Helper function to clean display values and add links
const cleanDisplayValueLocal = (value: any, columnName: string, lead?: any): React.ReactNode => {
  // Special handling for Name column
  if (columnName === 'Name' && lead) {
    const mergedName = getMergedName(lead);
    return mergedName;
  }
  
  // Use the enhanced cleaning utility for all other columns
  return cleanDisplayValue(value, columnName);
};

// Helper function to get contact details from lead data
const getContactDetails = (lead: any): {
  phone: string | null;
  email: string | null;
  website: string | null;
  linkedin: string | null;
  facebook: string | null;
  twitter: string | null;
  instagram: string | null;
} => {
  const data = lead.originalData || {};
  
  // Common field names for contact information
  const phoneFields = ['Phone', 'Phone Number', 'Mobile', 'Contact Number', 'Tel', 'Telephone'];
  const emailFields = ['Email', 'Email Address', 'Contact Email', 'Business Email'];
  const websiteFields = ['Website', 'Company Website', 'URL', 'Web Address'];
  const linkedinFields = ['LinkedIn', 'LinkedIn URL', 'LinkedIn Profile', 'Social LinkedIn'];
  const facebookFields = ['Facebook', 'Facebook URL', 'Facebook Page', 'Social Facebook'];
  const twitterFields = ['Twitter', 'Twitter URL', 'Twitter Handle', 'Social Twitter'];
  const instagramFields = ['Instagram', 'Instagram URL', 'Instagram Handle', 'Social Instagram'];
  
  const getFieldValue = (fields: string[]): string | null => {
    for (const field of fields) {
      if (data[field as keyof typeof data]) return data[field as keyof typeof data] as string;
    }
    return null;
  };
  
  return {
    phone: getFieldValue(phoneFields),
    email: getFieldValue(emailFields),
    website: getFieldValue(websiteFields),
    linkedin: getFieldValue(linkedinFields),
    facebook: getFieldValue(facebookFields),
    twitter: getFieldValue(twitterFields),
    instagram: getFieldValue(instagramFields),
  };
};

// Helper function to check if call outcome columns exist in the data
const hasCallOutcomeColumns = (leads: Lead[]): boolean => {
  if (!leads || leads.length === 0) return false;
  
  const callOutcomeColumns = ['Contact Status', 'Call Outcome', 'Lead Qualification'];
  const sampleLead = leads[0];
  
  return callOutcomeColumns.some(column => 
    sampleLead.originalData && sampleLead.originalData.hasOwnProperty(column)
  );
};

// Social media icon component
const SocialIcon: React.FC<{ type: string; url: string }> = ({ type, url }) => {
  const iconProps = { sx: { fontSize: 16 } };
  
  const icons = {
    linkedin: <LinkedInIcon {...iconProps} />,
    facebook: <FacebookIcon {...iconProps} />,
    twitter: <TwitterIcon {...iconProps} />,
    instagram: <InstagramIcon {...iconProps} />,
    website: <WebsiteIcon {...iconProps} />,
  };
  
  return (
    <IconButton
      size="small"
      component="a"
      href={url.startsWith('http') ? url : `https://${url}`}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ p: 0.5 }}
    >
      {icons[type as keyof typeof icons] || <WebsiteIcon {...iconProps} />}
    </IconButton>
  );
};

const SalesDashboard: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  
  // Main state
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Assignment detail state
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [assignmentLeads, setAssignmentLeads] = useState<Lead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsDialogOpen, setLeadsDialogOpen] = useState(false);
  
  // Lead editing state
  const [editingLeads, setEditingLeads] = useState<{ [leadId: string]: LeadEdit }>({});
  const [submittingLeads, setSubmittingLeads] = useState<Set<string>>(new Set());
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  
  // Column width state
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({});
  const [autoFittedColumns, setAutoFittedColumns] = useState<{ [key: string]: boolean }>({});
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // 9 Color variations cycling system (20% opacity background, solid text)
  const pillColors = [
    { bg: '#FF6D8D', text: '#FF6D8D', opacity: 0.2 }, // Button 1: Red
    { bg: '#60C98C', text: '#60C98C', opacity: 0.2 }, // Button 2: Green
    { bg: '#E6C717', text: '#E6C717', opacity: 0.2 }, // Button 3: Yellow
    { bg: '#942FED', text: '#942FED', opacity: 0.2 }, // Button 4: Purple
    { bg: '#43BBEF', text: '#43BBEF', opacity: 0.2 }, // Button 5: Blue
    { bg: '#E61717', text: '#E61717', opacity: 0.2 }, // Button 6: Red
    { bg: '#FFC400', text: '#FFC400', opacity: 0.2 }, // Button 7: Yellow
    { bg: '#FF7700', text: '#FF7700', opacity: 0.2 }, // Button 8: Orange
    { bg: '#1760E6', text: '#1760E6', opacity: 0.2 }  // Button 9: Blue
  ];

  // Generate pill color based on order (cycles through colors 1-9 in order)
  const getPillColor = (index: number) => {
    // Use modulo to cycle through colors in order: 0, 1, 2, 3, 4, 5, 6, 7, 8, 0, 1, 2...
    const colorIndex = index % pillColors.length;
    const color = pillColors[colorIndex];
    
    // Ensure we always have a visible background color
    const backgroundColor = color.bg || '#424242'; // Fallback to dark gray if undefined
    const textColor = color.text || '#FFFFFF'; // Fallback to white if undefined
    const opacity = color.opacity || 0.2; // Use specified opacity or default to 0.2
    
    // Convert hex to rgba for opacity support
    const hexToRgba = (hex: string, opacity: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    };
    
    return {
      backgroundColor: hexToRgba(backgroundColor, opacity),
      color: backgroundColor, // Use the solid background color for text (not the rgba version)
      border: `1px solid ${backgroundColor}`,
      borderRadius: '16px', // Pill shape with rounded corners
    };
  };
  
  // Load active assignments for sales
  const loadAssignments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await clientAPI.getEditorAssignments();
      
      if (response.data.success) {
        setAssignments(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to load assignments');
      }
    } catch (err: any) {
      console.error('Error loading assignments:', err);
      setError(err.response?.data?.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };
  
  // Load leads for an assignment
  const loadAssignmentLeads = async (assignment: Assignment) => {
    try {
      setLeadsLoading(true);
      setSelectedAssignment(assignment);
      setLeadsDialogOpen(true);
      setError(null);
      
      const response = await clientAPI.getAssignmentLeads(assignment._id);
      
      if (response.data.success) {
        setAssignmentLeads(response.data.data.leads || []);
      } else {
        setError(response.data.message || 'Failed to load leads');
      }
    } catch (err: any) {
      console.error('Error loading assignment leads:', err);
      setError(err.response?.data?.message || 'Failed to load assignment leads');
    } finally {
      setLeadsLoading(false);
    }
  };
  
  // Submit individual lead
  const submitLead = async (leadId: string) => {
    if (!selectedAssignment) return;
    
    const leadEdit = editingLeads[leadId];
    if (!leadEdit || !leadEdit.newContactStatus || !leadEdit.newCallOutcome || !leadEdit.newLeadQualification) {
      setError('All three fields (Contact Status, Call Outcome, Lead Qualification) are required');
      return;
    }
    
    try {
      setSubmittingLeads(prev => new Set(prev).add(leadId));
      setError(null);
      
      const response = await clientAPI.submitLead(
        selectedAssignment._id,
        leadId,
        leadEdit
      );
      
             if (response.data.success) {
         // Update the lead in the local state to mark it as completed
         setAssignmentLeads(prev => prev.map(lead => 
           lead.leadId === leadId 
             ? {
                 ...lead,
                 contactStatus: leadEdit.newContactStatus,
                 callOutcome: leadEdit.newCallOutcome,
                 leadQualification: leadEdit.newLeadQualification,
                 _completedBy: user?._id,
                 _isCompleted: true,
                 _completedAt: new Date().toISOString(),
                 // Mark the new fields as completed with the submitted values
                 newContactStatus: leadEdit.newContactStatus,
                 newCallOutcome: leadEdit.newCallOutcome,
                 newLeadQualification: leadEdit.newLeadQualification
               }
             : lead
         ));
         
         // Update assignment remaining count
         const newRemainingCount = response.data.data.remainingForEditor;
         setSelectedAssignment(prev => prev ? {
           ...prev,
           remainingLeads: newRemainingCount
         } : null);
         
         // Update assignments list
         setAssignments(prev => prev.map(assignment => 
           assignment._id === selectedAssignment._id 
             ? { ...assignment, remainingLeads: newRemainingCount }
             : assignment
         ).filter(assignment => assignment.remainingLeads > 0)); // Remove completed assignments
         
         // Clear editing state for this lead
         setEditingLeads(prev => {
           const updated = { ...prev };
           delete updated[leadId];
           return updated;
         });
         
         // Remove from selected
         setSelectedLeads(prev => {
           const updated = new Set(prev);
           updated.delete(leadId);
           return updated;
         });
         
         // If no leads remaining, close dialog and remove from assignments
         if (newRemainingCount === 0) {
           setLeadsDialogOpen(false);
           setSelectedAssignment(null);
         }
       } else {
        setError(response.data.message || 'Failed to submit lead');
      }
    } catch (err: any) {
      console.error('Error submitting lead:', err);
      setError(err.response?.data?.message || 'Failed to submit lead');
    } finally {
      setSubmittingLeads(prev => {
        const updated = new Set(prev);
        updated.delete(leadId);
        return updated;
      });
    }
  };
  
  // Handle lead field changes
  const handleLeadFieldChange = (leadId: string, field: keyof LeadEdit, value: string) => {
    console.log('🔧 Field change:', { leadId, field, value });
    setEditingLeads(prev => ({
      ...prev,
      [leadId]: {
        ...prev[leadId],
        [field]: value
      }
    }));
  };
  
  // Handle column resizing
  const handleColumnResize = (columnId: string, width: number) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnId]: Math.max(width, 100) // Minimum width of 100px
    }));
  };

  // Get column width
  const getColumnWidth = (columnId: string) => {
    return columnWidths[columnId] || 180; // Default width
  };

  // Auto-fit column to content width (optimized for performance)
  const autoFitColumn = (columnId: string) => {
    const isCurrentlyFitted = autoFittedColumns[columnId];
    
    if (isCurrentlyFitted) {
      // Collapse back to default width
      requestAnimationFrame(() => {
        handleColumnResize(columnId, 180);
        setAutoFittedColumns(prev => ({
          ...prev,
          [columnId]: false
        }));
      });
    } else {
      // Calculate content width by finding the longest content in the column
      requestAnimationFrame(() => {
        let maxWidth = 100; // Minimum width
        const headerLength = columnId.length * 10 + 40; // Approximate header width
        maxWidth = Math.max(maxWidth, headerLength);
        
        // Get visible data rows - only sample first 100 rows for performance
        const sampleSize = Math.min(100, assignmentLeads.length);
        for (let i = 0; i < sampleSize; i++) {
          const lead = assignmentLeads[i];
          const cellValue = String(lead.originalData?.[columnId] || '');
          const contentWidth = cellValue.length * 8 + 30; // Approximate content width
          maxWidth = Math.max(maxWidth, contentWidth);
        }
        
        // Cap maximum width at 500px for usability
        maxWidth = Math.min(maxWidth, 500);
        
        handleColumnResize(columnId, maxWidth);
        setAutoFittedColumns(prev => ({
          ...prev,
          [columnId]: true
        }));
      });
    }
  };
  
  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Resizable column header component
  const ResizableTableCell: React.FC<any> = ({ 
    children, 
    columnId, 
    className,
    sx = {},
    ...props 
  }) => {
    const [isResizing, setIsResizing] = useState(false);

    const handleDoubleClick = () => {
      autoFitColumn(columnId);
    };

    const handleMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      const startX = e.clientX;
      const startWidth = getColumnWidth(columnId);
      setIsResizing(true);

      const handleMouseMove = (e: MouseEvent) => {
        const diff = e.clientX - startX;
        const newWidth = Math.max(startWidth + diff, 100);
        handleColumnResize(columnId, newWidth);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    };

    return (
      <TableCell
        className={className}
        {...props}
        onDoubleClick={handleDoubleClick}
        sx={{
          ...sx,
          width: getColumnWidth(columnId),
          minWidth: getColumnWidth(columnId),
          maxWidth: getColumnWidth(columnId),
          position: 'relative',
          userSelect: isResizing ? 'none' : 'auto',
          overflow: 'visible',
          cursor: 'default',
          transition: 'width 0.2s ease-out, min-width 0.2s ease-out, max-width 0.2s ease-out',
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
          }
        }}
        title="Double-click to auto-fit column width"
      >
        {children}
        <Box
          sx={{
            position: 'absolute',
            right: -2,
            top: 0,
            bottom: 0,
            width: '8px',
            cursor: 'col-resize',
            backgroundColor: isResizing ? theme.palette.primary.main : 'transparent',
            transition: 'background-color 0.15s ease',
            '&:hover': {
              backgroundColor: theme.palette.primary.main + '66',
            },
            zIndex: 10,
            borderRadius: '0 2px 2px 0'
          }}
          onMouseDown={handleMouseDown}
        />
      </TableCell>
    );
  };
  
  // Get current page leads
  const currentPageLeads = useMemo(() => {
    const start = page * rowsPerPage;
    return assignmentLeads.slice(start, start + rowsPerPage);
  }, [assignmentLeads, page, rowsPerPage]);
  
  // Load assignments on mount
  useEffect(() => {
    loadAssignments();
  }, []);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading your assignments...
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box 
      className={theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}
      sx={{ 
        p: 3, 
        backgroundColor: theme.palette.mode === 'dark' ? '#081028' : 'transparent',
        minHeight: '100vh'
      }}
    >
      <Container maxWidth={false}>
      <MetaTags 
        title="Sales Dashboard - Process Assigned Leads"
        description="Process and manage your assigned company leads"
      />
      
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Lead Processing Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Process assigned company leads and update contact information
        </Typography>
      </Box>
      
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Active Assignments - Company Cards Grid */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Active Assignments ({assignments.length})
      </Typography>
      
      {assignments.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No active assignments
          </Typography>
          <Typography color="text.secondary">
            You currently have no companies assigned for lead processing.
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {assignments.map((assignment) => {
                         // Create a mock company data structure similar to Admin dashboard
             const sampleLead = assignmentLeads.length > 0 ? assignmentLeads[0] : null;
             const contactDetails = sampleLead ? getContactDetails(sampleLead) : {
               phone: null,
               email: null,
               website: null,
               linkedin: null,
               facebook: null,
               twitter: null,
               instagram: null
             };
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={4} key={assignment._id}>
                <Card 
                  className={`${theme.palette.mode === 'dark' ? 'company-card-dark-mode' : 'company-card-light-mode'} company-card-base-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}
                  onClick={() => loadAssignmentLeads(assignment)}
                >
                  <CardContent className="company-card-content-light">
                    {/* Header with Company Name and Chip */}
                    <Box className="company-card-header-light">
                      <Box className="company-card-header-flex-light">
                        <Box className="company-card-name-container-light">
                          <Typography 
                            variant="h6" 
                            className={`company-card-name-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}
                            title={assignment.leadCompanyName || assignment.companyName}
                          >
                            {assignment.leadCompanyName || assignment.companyName}
                          </Typography>
                        </Box>
                        <Chip 
                          className={`company-leads-chip-${theme.palette.mode === 'dark' ? 'dark' : 'light'}`}
                          label={`${assignment.remainingLeads} left`}
                          size="small" 
                          sx={{ 
                            ml: 1,
                            backgroundColor: '#1B4672 !important',
                            color: '#ffffff !important',
                            fontWeight: 'bold',
                            '& .MuiChip-label': {
                              color: '#ffffff !important'
                            }
                          }}
                        />
                      </Box>
                      <Divider sx={{ borderColor: theme.palette.mode === 'dark' ? '#C8C8C8' : theme.palette.divider }} />
                    </Box>

                    {/* Industry Title and Completion Status - Below Divider */}
                    <Box sx={{ mb: 1 }}>
                      {/* Industry Title */}
                      <Typography 
                        variant="body2"
                        className={`editor-industry-text ${theme.palette.mode === 'dark' ? 'dark-mode' : ''}`}
                      >
                        {assignment.companyId.companyIndustry || 'N/A'}
                      </Typography>
                      
                      {/* Completion Status - Below Industry */}
                      <Typography 
                        variant="body2"
                        className={`editor-status-text ${theme.palette.mode === 'dark' ? 'dark-mode' : ''}`}
                      >
                        {assignment.totalLeads - assignment.remainingLeads}/{assignment.totalLeads} completed
                      </Typography>
                    </Box>

                    {/* Three Pills: Industry, Assignment Type, Priority */}
                    <Box className="pill-container-wrapper-light" sx={{ mt: 2 }}>
                      <Box className="pill-container-wrapper">
                        <div 
                          className="pill-container-scrollbar company-card-pill-scrollbar editor-pill-container"
                          style={{
                            paddingBottom: '18px',
                            paddingRight: '12px',
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '8px',
                            flexWrap: 'nowrap',
                            justifyContent: 'flex-start',
                            alignItems: 'center'
                          }}
                        >
                          {(() => {
                            const pills: JSX.Element[] = [];
                            
                            // Pill 1: Industry name
                            const industryName = assignment.companyId.companyIndustry || 'N/A';
                            const pillStyle1 = getPillColor(0);
                            pills.push(
                              <MarqueeChip
                                key="industry"
                                label={String(industryName)}
                                size="small"
                                maxWidth={90}
                                textColor={pillStyle1.color}
                                sx={{ 
                                  backgroundColor: pillStyle1.backgroundColor,
                                  color: pillStyle1.color,
                                  border: pillStyle1.border,
                                  fontSize: '0.7rem',
                                  width: 90,
                                  height: 24,
                                  minHeight: 24,
                                  '& .MuiChip-label': {
                                    py: 0.5,
                                    px: 1,
                                    fontWeight: 500,
                                    textAlign: 'center',
                                    lineHeight: 1.2
                                  }
                                }}
                              />
                            );
                            
                            // Pill 2: Follow-Up or Unassigned
                            const assignmentType = assignment.isReassignment ? 'Follow-Up' : 'Unassigned';
                            const pillStyle2 = getPillColor(1);
                            pills.push(
                              <MarqueeChip
                                key="assignment-type"
                                label={assignmentType}
                                size="small"
                                maxWidth={90}
                                textColor={pillStyle2.color}
                                sx={{ 
                                  backgroundColor: pillStyle2.backgroundColor,
                                  color: pillStyle2.color,
                                  border: pillStyle2.border,
                                  fontSize: '0.7rem',
                                  width: 90,
                                  height: 24,
                                  minHeight: 24,
                                  '& .MuiChip-label': {
                                    py: 0.5,
                                    px: 1,
                                    fontWeight: 500,
                                    textAlign: 'center',
                                    lineHeight: 1.2
                                  }
                                }}
                              />
                            );
                            
                            // Pill 3: Priority (High/Medium/Low)
                            const priority = assignment.priority ? assignment.priority.toUpperCase() : 'MEDIUM';
                            const pillStyle3 = getPillColor(2);
                            pills.push(
                              <MarqueeChip
                                key="priority"
                                label={priority}
                                size="small"
                                maxWidth={90}
                                textColor={pillStyle3.color}
                                sx={{ 
                                  backgroundColor: pillStyle3.backgroundColor,
                                  color: pillStyle3.color,
                                  border: pillStyle3.border,
                                  fontSize: '0.7rem',
                                  width: 90,
                                  height: 24,
                                  minHeight: 24,
                                  '& .MuiChip-label': {
                                    py: 0.5,
                                    px: 1,
                                    fontWeight: 500,
                                    textAlign: 'center',
                                    lineHeight: 1.2
                                  }
                                }}
                              />
                            );
                            
                            return pills;
                          })()}
                        </div>
                      </Box>
                    </Box>
                    
                    {/* Process Leads Button - Centered */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 'auto' }}>
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        disabled={leadsLoading}
                        sx={{ 
                          px: 4,
                          py: 1.5,
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          borderRadius: '8px',
                          textTransform: 'none',
                          backgroundColor: 'transparent !important',
                          color: '#2A5A8A !important',
                          border: '1px solid #2A5A8A !important',
                          boxShadow: 'none',
                          '& .MuiButton-startIcon': {
                            color: '#2A5A8A !important',
                            '& svg': {
                              color: '#2A5A8A !important',
                              fill: '#2A5A8A !important'
                            }
                          },
                          '& svg': {
                            color: '#2A5A8A !important',
                            fill: '#2A5A8A !important'
                          },
                          '&:hover': {
                            backgroundColor: 'transparent !important',
                            border: '1px solid #2A5A8A !important',
                            boxShadow: 'none',
                            transform: 'none',
                            '& svg': {
                              color: '#2A5A8A !important',
                              fill: '#2A5A8A !important'
                            }
                          }
                        }}
                      >
                        Process Leads
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
      
      {/* Leads Processing Dialog */}
      <Dialog
        open={leadsDialogOpen}
        onClose={() => setLeadsDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">
                Process Leads: {selectedAssignment?.leadCompanyName || selectedAssignment?.companyName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedAssignment?.remainingLeads} leads remaining
              </Typography>
            </Box>
            <IconButton onClick={() => setLeadsDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, overflow: 'auto' }}>
          {leadsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading leads...
              </Typography>
            </Box>
          ) : assignmentLeads.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No leads found
              </Typography>
            </Box>
          ) : (
            <Box sx={{ 
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden'
            }}>
              <TableContainer 
                component={Paper}
                className={`client-table-container ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                sx={{ 
                  maxHeight: 'calc(90vh - 200px)',
                  borderRadius: 2,
                  boxShadow: 2,
                  backgroundColor: theme.palette.mode === 'dark' ? '#0B1739' : '#ffffff'
                }}
              >
                <Table className={`client-table ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} stickyHeader size="small">
                  <TableHead>
                    <TableRow 
                      className={`client-table-header ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                      sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}
                    >
                      {/* Specific columns with proper theming */}
                      <ResizableTableCell columnId="First Name" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        First Name
                      </ResizableTableCell>
                      <ResizableTableCell columnId="Last Name" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Last Name
                      </ResizableTableCell>
                      <ResizableTableCell columnId="Name" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Name
                      </ResizableTableCell>
                      <ResizableTableCell columnId="Work Email" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Work Email
                      </ResizableTableCell>
                      <ResizableTableCell columnId="Job Function" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Job Function
                      </ResizableTableCell>
                      <ResizableTableCell columnId="Job Title" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Job Title
                      </ResizableTableCell>
                      <ResizableTableCell columnId="Company Name" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Company Name
                      </ResizableTableCell>
                      <ResizableTableCell columnId="Phone" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Phone
                      </ResizableTableCell>
                      <ResizableTableCell columnId="Company City" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Company City
                      </ResizableTableCell>
                      <ResizableTableCell columnId="Company Country" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Company Country
                      </ResizableTableCell>
                      <ResizableTableCell columnId="Company Facebook" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Company Facebook
                      </ResizableTableCell>
                      <ResizableTableCell columnId="Company Industry" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Company Industry
                      </ResizableTableCell>
                      <ResizableTableCell columnId="Company LinkedIn" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Company LinkedIn
                      </ResizableTableCell>
                      <ResizableTableCell columnId="Company Phone" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Company Phone
                      </ResizableTableCell>
                      <ResizableTableCell columnId="Company Twitter" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Company Twitter
                      </ResizableTableCell>
                      <ResizableTableCell columnId="person Country" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        person Country
                      </ResizableTableCell>
                      <ResizableTableCell columnId="LinkedIn URL" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        LinkedIn URL
                      </ResizableTableCell>
                       
                      {/* Sales Fields */}
                      {selectedAssignment?.isReassignment ? (
                        <>
                          {/* Previous values */}
                          <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            Prev. Contact Status
                          </TableCell>
                          <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            Prev. Call Outcome
                          </TableCell>
                          <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            Prev. Lead Qualification
                          </TableCell>
                          <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            Completed By
                          </TableCell>
                          {/* New editable fields */}
                          <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            New Contact Status
                          </TableCell>
                          <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            New Call Outcome
                          </TableCell>
                          <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            New Lead Qualification
                          </TableCell>
                        </>
                      ) : (
                        <>
                          {/* First assignment - only editable fields */}
                          <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            Contact Status
                          </TableCell>
                          <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            Call Outcome
                          </TableCell>
                          <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            Lead Qualification
                          </TableCell>
                        </>
                      )}
                      
                      {/* Time-based columns - positioned before call outcome columns if they exist, otherwise at the end */}
                      {hasCallOutcomeColumns(assignmentLeads) ? (
                        <>
                          <ResizableTableCell columnId="Last 7 Days" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            Last 7 Days
                          </ResizableTableCell>
                          <ResizableTableCell columnId="Last 30 Days" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            Last 30 Days
                          </ResizableTableCell>
                          <ResizableTableCell columnId="Last 90 Days" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            Last 90 Days
                          </ResizableTableCell>
                        </>
                      ) : null}
                      
                      {/* Time-based columns - positioned at the end if call outcome columns don't exist */}
                      {!hasCallOutcomeColumns(assignmentLeads) ? (
                        <>
                          <ResizableTableCell columnId="Last 7 Days" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            Last 7 Days
                          </ResizableTableCell>
                          <ResizableTableCell columnId="Last 30 Days" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            Last 30 Days
                          </ResizableTableCell>
                          <ResizableTableCell columnId="Last 90 Days" className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                            Last 90 Days
                          </ResizableTableCell>
                        </>
                      ) : null}
                      
                      <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                                         {currentPageLeads.map((lead) => {
                       const isEditing = editingLeads[lead.leadId];
                       const isSubmitting = submittingLeads.has(lead.leadId);
                       // Check if this lead is completed in the current assignment
                       const isCompleted = lead._isCompleted === true;
                      
                                             return (
                         <TableRow 
                           key={lead.leadId}
                           className={`client-table-row ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                           sx={{ 
                             '&:hover': { backgroundColor: 'transparent' },
                             cursor: 'default'
                           }}
                         >
                          {/* Specific data columns with proper theming */}
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['First Name'], 'First Name')}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['Last Name'], 'Last Name')}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['Name'], 'Name', lead.originalData)}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {cleanDisplayValueLocal(lead.originalData['Work Email'], 'Work Email')}
                              </Typography>
                              {lead.originalData._emailValidationStatus === 'valid' && (
                                <Tooltip title="Verified Email" arrow>
                                  <CheckCircleIcon 
                                    sx={{ 
                                      fontSize: 16, 
                                      color: '#4CAF50',
                                      flexShrink: 0
                                    }} 
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['Job Function'], 'Job Function')}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['Job Title'], 'Job Title')}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['Company Name'], 'Company Name')}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['Phone'], 'Phone')}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['Company City'], 'Company City')}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['Company Country'], 'Company Country')}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['Company Facebook'], 'Company Facebook')}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['Company Industry'], 'Company Industry')}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['Company LinkedIn'], 'Company LinkedIn')}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['Company Phone'], 'Company Phone')}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['Company Twitter'], 'Company Twitter')}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['person Country'], 'person Country')}
                            </Typography>
                          </TableCell>
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {cleanDisplayValueLocal(lead.originalData['LinkedIn URL'], 'LinkedIn URL')}
                            </Typography>
                          </TableCell>
                          
                          {/* Sales Fields based on assignment type */}
                          {selectedAssignment?.isReassignment ? (
                            <>
                              {/* Previous values (read-only) */}
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {lead.prevContactStatus ? getOptionLabel(CONTACT_STATUS_OPTIONS, lead.prevContactStatus) || lead.prevContactStatus : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {lead.prevCallOutcome ? getOptionLabel(CALL_OUTCOME_OPTIONS, lead.prevCallOutcome) || lead.prevCallOutcome : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {lead.prevLeadQualification ? getOptionLabel(LEAD_QUALIFICATION_OPTIONS, lead.prevLeadQualification) || lead.prevLeadQualification : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {lead.prevCompletedBy || '-'}
                                </Typography>
                              </TableCell>
                              
                              {/* New editable fields */}
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                {isCompleted ? (
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {getOptionLabel(CONTACT_STATUS_OPTIONS, lead.newContactStatus) || lead.newContactStatus || '-'}
                                  </Typography>
                                ) : (
                                  <CustomSelect
                                    value={isEditing?.newContactStatus || ''}
                                    onChange={(value) => handleLeadFieldChange(lead.leadId, 'newContactStatus', value)}
                                    options={CONTACT_STATUS_OPTIONS}
                                    placeholder="Select Contact Status..."
                                    disabled={isSubmitting}
                                    size="small"
                                  />
                                )}
                              </TableCell>
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                {isCompleted ? (
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {getOptionLabel(CALL_OUTCOME_OPTIONS, lead.newCallOutcome) || lead.newCallOutcome || '-'}
                                  </Typography>
                                ) : (
                                  <CustomSelect
                                    value={isEditing?.newCallOutcome || ''}
                                    onChange={(value) => handleLeadFieldChange(lead.leadId, 'newCallOutcome', value)}
                                    options={CALL_OUTCOME_OPTIONS}
                                    placeholder="Select Call Outcome..."
                                    disabled={isSubmitting}
                                    size="small"
                                  />
                                )}
                              </TableCell>
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                {isCompleted ? (
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {getOptionLabel(LEAD_QUALIFICATION_OPTIONS, lead.newLeadQualification) || lead.newLeadQualification || '-'}
                                  </Typography>
                                ) : (
                                  <CustomSelect
                                    value={isEditing?.newLeadQualification || ''}
                                    onChange={(value) => handleLeadFieldChange(lead.leadId, 'newLeadQualification', value)}
                                    options={LEAD_QUALIFICATION_OPTIONS}
                                    placeholder="Select Lead Qualification..."
                                    disabled={isSubmitting}
                                    size="small"
                                  />
                                )}
                              </TableCell>
                            </>
                          ) : (
                            <>
                              {/* First-time assignment - only editable fields */}
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                {isCompleted ? (
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {getOptionLabel(CONTACT_STATUS_OPTIONS, lead.newContactStatus) || lead.newContactStatus || '-'}
                                  </Typography>
                                ) : (
                                  <CustomSelect
                                    value={isEditing?.newContactStatus || ''}
                                    onChange={(value) => handleLeadFieldChange(lead.leadId, 'newContactStatus', value)}
                                    options={CONTACT_STATUS_OPTIONS}
                                    placeholder="Select Contact Status..."
                                    disabled={isSubmitting}
                                    size="small"
                                  />
                                )}
                              </TableCell>
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                {isCompleted ? (
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {getOptionLabel(CALL_OUTCOME_OPTIONS, lead.newCallOutcome) || lead.newCallOutcome || '-'}
                                  </Typography>
                                ) : (
                                  <CustomSelect
                                    value={isEditing?.newCallOutcome || ''}
                                    onChange={(value) => handleLeadFieldChange(lead.leadId, 'newCallOutcome', value)}
                                    options={CALL_OUTCOME_OPTIONS}
                                    placeholder="Select Call Outcome..."
                                    disabled={isSubmitting}
                                    size="small"
                                  />
                                )}
                              </TableCell>
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                {isCompleted ? (
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {getOptionLabel(LEAD_QUALIFICATION_OPTIONS, lead.newLeadQualification) || lead.newLeadQualification || '-'}
                                  </Typography>
                                ) : (
                                  <CustomSelect
                                    value={isEditing?.newLeadQualification || ''}
                                    onChange={(value) => handleLeadFieldChange(lead.leadId, 'newLeadQualification', value)}
                                    options={LEAD_QUALIFICATION_OPTIONS}
                                    placeholder="Select Lead Qualification..."
                                    disabled={isSubmitting}
                                    size="small"
                                  />
                                )}
                              </TableCell>
                            </>
                          )}
                          
                          {/* Time-based columns - positioned before call outcome columns if they exist, otherwise at the end */}
                          {hasCallOutcomeColumns(assignmentLeads) ? (
                            <>
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {cleanDisplayValueLocal(lead.originalData['Last 7 Days'], 'Last 7 Days')}
                                </Typography>
                              </TableCell>
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {cleanDisplayValueLocal(lead.originalData['Last 30 Days'], 'Last 30 Days')}
                                </Typography>
                              </TableCell>
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {cleanDisplayValueLocal(lead.originalData['Last 90 Days'], 'Last 90 Days')}
                                </Typography>
                              </TableCell>
                            </>
                          ) : null}
                          
                          {/* Time-based columns - positioned at the end if call outcome columns don't exist */}
                          {!hasCallOutcomeColumns(assignmentLeads) ? (
                            <>
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {cleanDisplayValueLocal(lead.originalData['Last 7 Days'], 'Last 7 Days')}
                                </Typography>
                              </TableCell>
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {cleanDisplayValueLocal(lead.originalData['Last 30 Days'], 'Last 30 Days')}
                                </Typography>
                              </TableCell>
                              <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {cleanDisplayValueLocal(lead.originalData['Last 90 Days'], 'Last 90 Days')}
                                </Typography>
                              </TableCell>
                            </>
                          ) : null}
                          
                          {/* Actions */}
                          <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                            {isCompleted ? (
                              <Chip
                                label="Completed"
                                color="success"
                                size="small"
                                icon={<CompletedIcon />}
                              />
                            ) : (
                              (() => {
                                const isDisabled = isSubmitting || 
                                  !isEditing || 
                                  !isEditing.newContactStatus || 
                                  !isEditing.newCallOutcome || 
                                  !isEditing.newLeadQualification;
                                console.log('🔘 Submit button state:', { 
                                  leadId: lead.leadId, 
                                  isSubmitting, 
                                  isEditing, 
                                  isDisabled,
                                  fields: isEditing ? {
                                    contactStatus: isEditing.newContactStatus,
                                    callOutcome: isEditing.newCallOutcome,
                                    leadQualification: isEditing.newLeadQualification
                                  } : null
                                });
                                return (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={() => submitLead(lead.leadId)}
                                    disabled={isDisabled}
                                  >
                                    {isSubmitting ? 'Saving...' : 'Submit'}
                                  </Button>
                                );
                              })()
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={assignmentLeads.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setLeadsDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => selectedAssignment && loadAssignmentLeads(selectedAssignment)}
            disabled={leadsLoading}
          >
            Refresh
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </Box>
  );
};

export default SalesDashboard;